import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ProcessRiskmapActions } from './process-riskmap.actions';
import { ProcessRiskmapService } from '../../service/process-riskmap.service';
import { MessageService } from 'primeng/api';
import { graphQlErrorMessage } from '../../shared/graphql-error.util';

@Injectable()
export class ProcessRiskmapEffects {

  loadRiskMapTree$ = createEffect(
    (rs: ProcessRiskmapService = inject(ProcessRiskmapService)) => {
      return this.actions$.pipe(

        ofType(ProcessRiskmapActions.loadRiskMapProcess),
        mergeMap(({ campaignId, cpts, parameters, fields, legalEntititiesIds }) =>
          rs.loadRiskmapProcess(campaignId, cpts, parameters, legalEntititiesIds).pipe(
            map((riskMap) => {
              return ProcessRiskmapActions.loadRiskMapProcessSuccess({
                riskMap,
                fields
              });
            }),
            catchError((error) => of(ProcessRiskmapActions.loadRiskMapProcessFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  getAllRiskmap$ = createEffect(
    (rs: ProcessRiskmapService = inject(ProcessRiskmapService)) => {
      return this.actions$.pipe(
        ofType(ProcessRiskmapActions.loadAllRiskMapProcess),
        mergeMap(({ campaignId, cpts, parameters, fields, legalEntititiesIds }) =>
          rs.getCptFilters(cpts).pipe(
            mergeMap((riskMap) => {
              const successAction = ProcessRiskmapActions.loadAllRiskMapProcessSuccess({
                riskMap: riskMap,
                fields
              });

              const anotherAction = ProcessRiskmapActions.loadRiskMapProcess({
                campaignId,
                cpts,
                parameters,
                fields,
                legalEntititiesIds
              });

              return [successAction, anotherAction]; // <-- Return multiple actions
            }),
            catchError((error) =>
              of(ProcessRiskmapActions.loadAllRiskMapProcessFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  updateStatus$ = createEffect(
    (
      rs: ProcessRiskmapService = inject(ProcessRiskmapService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(ProcessRiskmapActions.updateRiskMapProcessStatus),
        mergeMap(({ campaignId, cptId, legalEntityId, status, legalEntititiesIds }) =>
          rs.updateProcessRismapStatus(cptId, legalEntityId, status, campaignId, legalEntititiesIds).pipe(
            map((riskMap) => {
              return ProcessRiskmapActions.updateRiskMapProcessStatusSuccess({
                riskMap
              });
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Status Error',
                detail: graphQlErrorMessage(error, 'Unable to update the status'),
                life: 10000
              });
              return of(ProcessRiskmapActions.updateRiskMapProcessStatusFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  updateAu$ = createEffect(
    (
      rs: ProcessRiskmapService = inject(ProcessRiskmapService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(ProcessRiskmapActions.updateRiskMapAU),
        mergeMap(({ campaignId, legalEntityId, processId, legalEntititiesIds }) =>
          rs.updateAuProcessRiskmap(campaignId, legalEntityId, processId, legalEntititiesIds).pipe(
            map((riskMap) => {
              return ProcessRiskmapActions.updateRiskMapAUSuccess({
                riskMap
              });
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Auditable Universe Error',
                detail: graphQlErrorMessage(error, 'Unable to update the auditable universe'),
                life: 10000
              });
              return of(ProcessRiskmapActions.updateRiskMapAUFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  updateRisk$ = createEffect(
    (
      rs: ProcessRiskmapService = inject(ProcessRiskmapService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(ProcessRiskmapActions.updateRiskMapProcessRisk),
        mergeMap(({ campaignId, legalEntityId, processId, riskId, value, legalEntititiesIds }) =>
          rs.updateProcessRiskmapRisk(campaignId, legalEntityId, processId, riskId, value, legalEntititiesIds).pipe(
            mergeMap((riskMap) => [
              ProcessRiskmapActions.updateRiskMapProcessRiskSuccess({
                riskMap
              })
              // RiskmaptreeActions.processRelevanceTree({ campaignId, legalEntityId, processId }) // Seconda action
            ]),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Risk Map Error',
                detail: graphQlErrorMessage(error, 'Unable to update the risk map'),
                life: 10000
              });
              return of(ProcessRiskmapActions.updateRiskMapProcessRiskFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  exportRiskmapProcess$ = createEffect(
    (as: ProcessRiskmapService = inject(ProcessRiskmapService)) => {
      return this.actions$.pipe(
        ofType(ProcessRiskmapActions.exportRiskMapProcessTree),
        mergeMap(({ campaignId, cptIds, parameters, timeZone, legalEntititiesIds }) =>
          as.downloadProcessRiskmap(campaignId, cptIds, parameters, timeZone, legalEntititiesIds
          ).pipe(
            map((file) => {
              this.downloadFile(
                file!.data![0]!.content,
                file!.data![0]!.filename
              );
              return ProcessRiskmapActions.exportRiskMapProcessTreeSuccess({
                riskMap: file
              });
            }),
            catchError((error) => of(ProcessRiskmapActions.exportRiskMapProcessTreeFailure({ error })))
          )
        )
      )
    }
  )

  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type); // Cambia il tipo MIME
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }


  constructor(private actions$: Actions) { }
}



